import { css, keyframes } from '@emotion/css';
import { Colors } from '../../shared/styles/colors';
import { h3Style, p2Style } from '../../shared/styles/text';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../shared/components/Button';
import { useNavigator } from '../../routing/navigator';
import { Radius } from '../../shared/styles/tokens';

interface QRScannerMaskProps {
  error: boolean;
  setCodeModalVisible(value: boolean): void;
}

export const QRScannerMask = (props: QRScannerMaskProps): JSX.Element => {
  const { error, setCodeModalVisible } = props;
  const navigator = useNavigator();

  return (
    <div className={maskContainerStyle}>
      <div className={viewFinderContainerStyle}>
        <h3 className={titleStyle}>Scan the QR code</h3>
        <p className={hintStyle}>
          Point the camera at the code on the pedestal.
        </p>
        <div className={viewFinderStyle} aria-hidden="true">
          <span className={cornerStyle('top', 'left')} />
          <span className={cornerStyle('top', 'right')} />
          <span className={cornerStyle('bottom', 'left')} />
          <span className={cornerStyle('bottom', 'right')} />
          <hr className={viewFinderLine} />
        </div>
        <h3 className={errorStyle(error)} role="alert">
          Invalid Code
        </h3>
      </div>
      <div className={buttonsContainerStyle}>
        <Button
          variant={ButtonVariantEnum.OUTLINE}
          size={ButtonSizeEnum.MEDIUM}
          className={outlineButtonStyle}
          text="enter id"
          onClick={() => setCodeModalVisible(true)}
        />
        <Button
          variant={ButtonVariantEnum.TEXT}
          size={ButtonSizeEnum.MEDIUM}
          className={textButtonStyle}
          text="go back"
          onClick={navigator.toPrevious}
        />
      </div>
    </div>
  );
};

const scanLine = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.4; }
  10%, 90% { opacity: 1; }
  50% { transform: translateY(12.5rem); opacity: 1; }
`;

const maskContainerStyle = css`
  background-color: rgba(12, 32, 48, 0.55);
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const titleStyle = css`
  ${h3Style}
  color: ${Colors.WHITE};
  margin-bottom: 0.375rem;
`;

const hintStyle = css`
  ${p2Style}
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 1.5rem;
  text-align: center;
  padding: 0 2rem;
`;

const errorStyle = (error: boolean) => css`
  ${h3Style}
  color: ${Colors.WHITE};
  background: ${Colors.ERROR_DARK};
  padding: 0.375rem 1rem;
  border-radius: ${Radius.pill};
  margin-top: 1.5rem;
  visibility: ${error ? 'visible' : 'hidden'};
`;

const viewFinderContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const viewFinderStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 13rem;
  height: 13rem;
  border-radius: ${Radius.lg};
  box-shadow: 0 0 0 100vmax rgba(12, 32, 48, 0.25);
  position: relative;
  overflow: hidden;
`;

const cornerStyle = (v: 'top' | 'bottom', h: 'left' | 'right') => css`
  position: absolute;
  ${v}: 0;
  ${h}: 0;
  width: 2rem;
  height: 2rem;
  border-${v}: 3px solid ${Colors.WHITE};
  border-${h}: 3px solid ${Colors.WHITE};
  border-${v}-${h}-radius: ${Radius.lg};
`;

const viewFinderLine = css`
  border: 0;
  height: 2px;
  background: ${Colors.SOURCE_AVAILABLE};
  box-shadow: 0 0 12px ${Colors.SOURCE_AVAILABLE};
  margin: 0px;
  width: 80%;

  position: absolute;
  top: 0.25rem;
  animation: ${scanLine} 3s ease-in-out infinite;
`;

const outlineButtonStyle = css`
  color: ${Colors.WHITE};
  border-color: ${Colors.WHITE};
  background: rgba(255, 255, 255, 0.08);
  margin-bottom: 0.5rem;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.18);
    }
  }
`;

const textButtonStyle = css`
  color: ${Colors.WHITE};

  @media (hover: hover) {
    &:hover:not(:disabled) {
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

const buttonsContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  bottom: calc(6vh + env(safe-area-inset-bottom, 0px));
`;
